/**
 * @fileoverview URL Parameter Handler
 *
 * =============================================================================
 * PURPOSE & OVERVIEW
 * =============================================================================
 *
 * This utility provides consistent URL query parameter manipulation across the site.
 * It's designed to work with sort controls, filters, and pagination without requiring
 * page reloads (works great with HTMX).
 *
 * Key features:
 * - Update/remove URL parameters cleanly
 * - Auto-reset pagination when sort changes (user expects page 1 of new results)
 * - Apply user's saved sort preferences to navigation links
 * - Validate sort preferences against allowed sorts before applying
 *
 * =============================================================================
 * RELATIONSHIP WITH HrefSortInterceptor
 * =============================================================================
 *
 * This script works alongside HrefSortInterceptor:
 *
 * - HrefSortInterceptor: Fetches and manages the "allowed sorts" configuration,
 *   intercepts clicks on tagged links, handles caching of preferences
 *
 * - UrlParamHandler (this script): Provides the actual URL manipulation logic,
 *   validates preferences against allowed sorts, processes links on page load
 *
 * The scripts are designed to work together but can function independently:
 * - If HrefSortInterceptor isn't loaded, UrlParamHandler won't validate sorts
 *   (but also won't apply them - fail closed behavior)
 * - If UrlParamHandler isn't loaded, HrefSortInterceptor uses its basicApplySort
 *
 * =============================================================================
 * SORT PREFERENCE VALIDATION FLOW
 * =============================================================================
 *
 * When applyUserSortPreference() is called for a URL:
 *
 * 1. Get user's saved preference from localStorage
 *    - NONE -> Return original URL unchanged
 *
 * 2. Check if HrefSortInterceptor is ready with allowedSorts config
 *    - NOT READY -> Return original URL unchanged (fail closed)
 *
 * 3. Check if destination URL has ANY sort configuration
 *    - NO CONFIG -> Return original URL unchanged (page doesn't support sorting)
 *
 * 4. Validate user's preference against allowed sorts for destination URL
 *    - NOT ALLOWED -> Return original URL unchanged (don't guess defaults)
 *    - ALLOWED -> Apply preference to URL and return
 *
 * =============================================================================
 * WHY "FAIL CLOSED" INSTEAD OF APPLYING DEFAULTS
 * =============================================================================
 *
 * Previous behavior: If user's preference wasn't allowed, apply the first
 * allowed sort as a "default". This caused problems:
 *
 * - Links would change unexpectedly (user clicks "Landscapes" but URL shows
 *   different sort than what was authored in the link)
 * - If the original link already had a valid sort, it would get overwritten
 * - Confusing UX when sort changed without user action
 *
 * Current behavior: If preference isn't allowed, leave URL unchanged. This:
 * - Respects the original link's authored intent
 * - Prevents unexpected behavior
 * - Is more predictable for users
 *
 * =============================================================================
 * USAGE EXAMPLES
 * =============================================================================
 */

window.UrlParamHandler = window.UrlParamHandler || {
  /**
   * Updates a URL with a new query parameter value.
   *
   * This is the core URL manipulation function. It handles:
   * - Adding new parameters
   * - Updating existing parameters
   * - Auto-resetting pagination when sort changes
   * - Preserving URL hash/fragment
   *
   * @param {string} url - The URL to update (can be absolute or relative)
   * @param {string} paramName - The name of the query parameter to update (e.g., 'sort', 'filter', 'page')
   * @param {string|number} paramValue - The new value for the parameter
   * @param {Object} options - Configuration options
   * @param {boolean} [options.resetPage=false] - Whether to reset pagination to page 1 (default: false, but true when paramName is 'sort')
   * @param {boolean} [options.preserveHash=true] - Whether to preserve URL hash/fragment (default: true)
   * @param {boolean} [options.removeEmpty=true] - Whether to remove parameters with empty values (default: true)
   *
   * @returns {string} The updated URL with the new parameter value
   *
   * @example
   * // Update sort parameter - automatically resets to page 1
   * const newUrl = UrlParamHandler.updateUrlParam(
   *   'https://example.com/products?page=3&category=art',
   *   'sort',
   *   'price-asc'
   * );
   * // Returns: 'https://example.com/products?page=1&category=art&sort=price-asc'
   *
   * @example
   * // Update a filter without resetting page
   * const newUrl = UrlParamHandler.updateUrlParam(
   *   '/products?sort=newest',
   *   'category',
   *   'paintings',
   *   { resetPage: false }
   * );
   * // Returns: '/products?sort=newest&category=paintings'
   */
  updateUrlParam: function (url, paramName, paramValue, options = {}) {
    // Set default options - note that resetPage defaults to true for 'sort' parameter
    const config = {
      resetPage: paramName === "sort", // Auto-reset page if sorting
      preserveHash: true,
      removeEmpty: true,
      defaultValue: null,
      ...options,
    };

    try {
      // Parse the URL (handles both absolute and relative URLs)
      let urlObj;
      try {
        urlObj = new URL(url, window.location.origin);
      } catch (e) {
        urlObj = new URL(url, window.location.origin);
      }

      // Get the search params
      const params = new URLSearchParams(urlObj.search);

      // Handle empty/null values - either use default or remove the param
      if (!paramValue && config.removeEmpty) {
        if (config.defaultValue) {
          paramValue = config.defaultValue;
        } else {
          params.delete(paramName);
          urlObj.search = params.toString();
          return urlObj.href;
        }
      }

      // Set the parameter value
      params.set(paramName, paramValue);

      // Reset pagination if needed (when changing sort, user expects page 1)
      if (config.resetPage && paramName !== "page") {
        params.set("page", "1");
      }

      // Update the URL with new search params
      urlObj.search = params.toString();

      // Handle hash preservation
      if (!config.preserveHash) {
        urlObj.hash = "";
      }

      return urlObj.href;
    } catch (error) {
      console.error("[UrlParamHandler] Error updating URL parameter:", error);
      return url; // Return original URL on error
    }
  },

  /**
   * Gets the current value of a query parameter from a URL
   *
   * @param {string} url - The URL to parse (defaults to current window location)
   * @param {string} paramName - The name of the parameter to get
   * @param {string} [defaultValue=null] - Default value if parameter doesn't exist
   *
   * @returns {string|null} The parameter value or default value
   *
   * @example
   * const sortValue = UrlParamHandler.getUrlParam(window.location.href, 'sort', 'newest');
   */
  getUrlParam: function (url, paramName, defaultValue = null) {
    try {
      const urlObj = new URL(url, window.location.origin);
      const params = new URLSearchParams(urlObj.search);
      return params.get(paramName) || defaultValue;
    } catch (error) {
      console.error("[UrlParamHandler] Error getting URL parameter:", error);
      return defaultValue;
    }
  },

  /**
   * Removes a query parameter from a URL
   *
   * @param {string} url - The URL to update
   * @param {string} paramName - The name of the parameter to remove
   *
   * @returns {string} The updated URL without the specified parameter
   *
   * @example
   * const newUrl = UrlParamHandler.removeUrlParam('/products?sort=newest&page=2', 'page');
   * // Returns: '/products?sort=newest'
   */
  removeUrlParam: function (url, paramName) {
    try {
      const urlObj = new URL(url, window.location.origin);
      const params = new URLSearchParams(urlObj.search);
      params.delete(paramName);
      urlObj.search = params.toString();
      return urlObj.href;
    } catch (error) {
      console.error("[UrlParamHandler] Error removing URL parameter:", error);
      return url;
    }
  },

  /**
   * Updates multiple query parameters at once
   *
   * @param {string} url - The URL to update
   * @param {Object} paramsObj - Object with key-value pairs of parameters to update
   * @param {Object} options - Configuration options (same as updateUrlParam)
   *
   * @returns {string} The updated URL with all new parameters
   *
   * @example
   * const newUrl = UrlParamHandler.updateMultipleParams(
   *   '/products',
   *   { sort: 'price-asc', category: 'art', color: 'blue' }
   * );
   */
  updateMultipleParams: function (url, paramsObj, options = {}) {
    try {
      const urlObj = new URL(url, window.location.origin);
      const params = new URLSearchParams(urlObj.search);

      // Update all parameters
      Object.entries(paramsObj).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });

      // Reset page if any parameter was 'sort'
      if ("sort" in paramsObj && options.resetPage !== false) {
        params.set("page", "1");
      }

      urlObj.search = params.toString();
      return urlObj.href;
    } catch (error) {
      console.error(
        "[UrlParamHandler] Error updating multiple parameters:",
        error,
      );
      return url;
    }
  },

  /**
   * Applies user's saved sort preference to a URL intelligently.
   *
   * ==========================================================================
   * VALIDATION LOGIC (IMPORTANT!)
   * ==========================================================================
   *
   * This function implements "fail closed" validation:
   *
   * 1. If no user preference saved -> Return original URL (nothing to apply)
   *
   * 2. If HrefSortInterceptor not ready -> Return original URL
   *    WHY: We can't validate without the allowed sorts config. Applying an
   *    invalid sort would cause 404 errors or broken pages.
   *
   * 3. If destination URL has no sort config -> Return original URL
   *    WHY: Some pages don't support sorting (Archives, About, etc.).
   *    We shouldn't add sort params to pages that don't handle them.
   *
   * 4. If user preference NOT in allowed list -> Return original URL
   *    WHY: The preference might be valid for other pages but not this one.
   *    Example: "high_to_low_price" is valid for /stock/ but not /sold/stock/
   *    (sold items don't have prices).
   *
   *    IMPORTANT: We DON'T fall back to a "default" sort. This was causing
   *    unexpected URL changes. Instead, we respect the original link's intent.
   *
   * 5. If user preference IS allowed -> Apply it to URL
   *
   * ==========================================================================
   *
   * @param {string} url - The URL to potentially update with sort preference
   * @param {string} [gridId='default'] - The grid ID to look up the sort preference for
   * @param {Object} options - Configuration options
   * @param {boolean} [options.forceApply=false] - If true, always apply preference even if sort exists in URL (still validates)
   * @param {boolean} [options.resetPage=true] - Whether to reset pagination when applying sort
   * @param {string} [options.sortType='product'] - The sort type (product, maker, etc.) for localStorage key
   *
   * @returns {string} The URL with sort preference applied (if valid) or original URL
   *
   * @example
   * // User preference is 'price-asc' and it's allowed for /products
   * const url = UrlParamHandler.applyUserSortPreference('/products?category=art');
   * // Returns: '/products?category=art&sort=price-asc&page=1'
   *
   * @example
   * // User preference is 'high_to_low_price' but NOT allowed for /sold/stock/
   * const url = UrlParamHandler.applyUserSortPreference('/sold/stock/?sort=artist_name_asc');
   * // Returns: '/sold/stock/?sort=artist_name_asc' (unchanged - original sort preserved)
   *
   * @example
   * // URL for a page that doesn't support sorting (no config)
   * const url = UrlParamHandler.applyUserSortPreference('/archives/');
   * // Returns: '/archives/' (unchanged)
   */
  applyUserSortPreference: function (url, gridId = "default", options = {}) {
    const config = {
      forceApply: false,
      resetPage: true,
      sortType: "product",
      ...options,
    };

    try {
      // =======================================================================
      // STEP 1: Get user's saved sort preference from localStorage
      // =======================================================================
      // Storage key pattern: {sortType}_grid_sort_user_pref_{gridId}
      // Example: "product_grid_sort_user_pref_default"
      const storageKey = `${config.sortType}_grid_sort_user_pref_${gridId}`;
      let userPreference = null;

      try {
        userPreference = siteStorage.get(storageKey);
      } catch (e) {
        console.warn(
          "[UrlParamHandler] Could not access localStorage for sort preference:",
          e,
        );
      }

      // Parse URL to get the destination path for validation
      const urlObj = new URL(url, window.location.origin);
      const urlPath = urlObj.pathname;
      const currentSort = this.getUrlParam(url, "sort");

      // =======================================================================
      // STEP 2: Check if destination URL has any sort configuration
      // =======================================================================
      // If the URL isn't in our allowed sorts config, this page doesn't support
      // sorting. Leave the URL completely unchanged.
      // Examples: /archives/, /about/, /contact/
      if (
        window.HrefSortInterceptor &&
        typeof window.HrefSortInterceptor.hasSortConfigForUrl === "function"
      ) {
        if (!window.HrefSortInterceptor.hasSortConfigForUrl(urlPath)) {
          // No sort config for this URL - page doesn't support sorting
          return url;
        }
      }

      // =======================================================================
      // STEP 3: Validate and apply user preference
      // =======================================================================
      if (userPreference) {
        // We have a user preference - now validate it

        // IMPORTANT: Default to false (fail closed)
        // Only apply preference if we can explicitly confirm it's allowed
        let isAllowed = false;

        if (
          window.HrefSortInterceptor &&
          typeof window.HrefSortInterceptor.isSortAllowedForUrl ===
            "function" &&
          window.HrefSortInterceptor.allowedSortsFetched
        ) {
          // HrefSortInterceptor is ready - use it to validate
          isAllowed = window.HrefSortInterceptor.isSortAllowedForUrl(
            urlPath,
            userPreference,
          );
        } else {
          // HrefSortInterceptor not ready - can't validate, so don't modify URL
          // This prevents race conditions where we apply sorts before knowing
          // what's valid for each page
          return url;
        }

        // Apply user preference ONLY if explicitly allowed
        if (isAllowed) {
          // Preference is valid for this URL - apply it
          if (currentSort !== userPreference) {
            // Only update if different from current sort
            return this.updateUrlParam(url, "sort", userPreference, {
              resetPage: config.resetPage,
            });
          }
          // URL already has the user's preferred sort - no change needed
          return url;
        } else {
          // =================================================================
          // User preference NOT allowed for this URL
          // =================================================================
          // IMPORTANT: We return the original URL unchanged.
          //
          // We do NOT apply a "default" sort because:
          // 1. The original link may already have a valid sort parameter
          // 2. Changing the URL unexpectedly is confusing for users
          // 3. The link author's intent should be respected
          //
          // Example scenario:
          // - User has preference: "high_to_low_price" (from browsing /stock/)
          // - They click link to /sold/stock/landscapes/?sort=artist_name_asc
          // - "high_to_low_price" is NOT allowed for /sold/stock/ (no prices)
          // - We leave URL as /sold/stock/landscapes/?sort=artist_name_asc
          // - NOT /sold/stock/landscapes/?sort=artist_name_asc (some random default)
          return url;
        }
      }

      // No user preference saved - return URL unchanged
      return url;
    } catch (error) {
      console.error(
        "[UrlParamHandler] Error applying user sort preference:",
        error,
      );
      return url; // Return original URL on error
    }
  },
};

/**
 * Global convenience function for use in href attributes.
 * Shorthand for UrlParamHandler.applyUserSortPreference()
 *
 * @param {string} url - The URL to update
 * @param {string} [gridId='default'] - Optional grid ID
 * @returns {string} URL with user's sort preference applied
 *
 * @example
 * <a href="{{ urlUpdateSortID('/products') }}">Products</a>
 */
window.urlUpdateSortID = function (url, gridId = "default") {
  return window.UrlParamHandler.applyUserSortPreference(url, gridId);
};

/**
 * Process all links with data-apply-sort-pref attribute.
 *
 * This updates href attributes on tagged links so that:
 * 1. Right-click "Open in new tab" works with the correct sort
 * 2. Crawlers see the correct URLs
 * 3. No JavaScript needed at click time (progressive enhancement)
 *
 * IMPORTANT: Waits for HrefSortInterceptor to be ready before processing.
 * This ensures we have the allowed sorts config for validation.
 *
 * @param {Element} [container=document] - Container to search for links (defaults to entire document)
 * @returns {Promise<void>}
 */
window.UrlParamHandler.processSortPrefLinks = async function (
  container = document,
) {
  // Wait for HrefSortInterceptor to be ready if it exists
  // This ensures allowedSorts config is loaded before we process links
  if (
    window.HrefSortInterceptor &&
    typeof window.HrefSortInterceptor.whenReady === "function"
  ) {
    await window.HrefSortInterceptor.whenReady();
  }

  // Find all tagged links and update their href attributes
  const links = container.querySelectorAll("a[data-apply-sort-pref]");
  links.forEach((link) => {
    const originalHref = link.getAttribute("href");
    if (originalHref) {
      // Get grid ID from data attribute value, default to 'default'
      const gridId = link.getAttribute("data-apply-sort-pref") || "default";
      const sortType = link.getAttribute("data-sort-type") || "product";
      const updatedHref = this.applyUserSortPreference(originalHref, gridId, {
        sortType,
      });
      // Only update if URL actually changed
      if (updatedHref !== originalHref) {
        link.setAttribute("href", updatedHref);
      }
    }
  });
};

/**
 * Initialize URL parameter handler.
 *
 * Sets up:
 * - Processing of existing links on page load (after HrefSortInterceptor is ready)
 * - HTMX event listeners to process dynamically loaded links
 */
window.UrlParamHandler.init = function () {
  // Process existing links on page load
  const processOnLoad = async () => {
    await this.processSortPrefLinks();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", processOnLoad);
  } else {
    // DOM already loaded - process immediately
    processOnLoad();
  }

  // Process links after HTMX swaps new content into the page
  // This ensures dynamically loaded links also get sort preferences applied
  document.body.addEventListener("htmx:afterSwap", (e) => {
    this.processSortPrefLinks(e.detail.target);
  });

  // Also handle OOB (Out of Band) swaps - content injected elsewhere in the DOM
  document.body.addEventListener("htmx:oobAfterSwap", (e) => {
    this.processSortPrefLinks(e.detail.target);
  });
};

// =============================================================================
// AUTO-INITIALIZATION
// =============================================================================
window.UrlParamHandler.init();
